<template>
  <PageLayout :title="$t('designHub.title')" :subtitle="$t('designHub.subtitle')">
    <template #actions>
      <button class="btn btn-outline-primary mr-2" @click="openImport">
        <i class="fas fa-file-import mr-1"></i> {{ $t('designHub.import_canva') }}
      </button>
      <button class="btn btn-primary" @click="openCreate">
        <i class="fas fa-plus mr-1"></i> {{ $t('designHub.create_new') }}
      </button>
    </template>

    <ContentCard>
      <div v-if="importJobs.length" class="hub-jobs">
        <div v-for="job in importJobs" :key="job.id" class="hub-job" :class="'is-' + job.status">
          <div class="hub-job-top">
            <strong>{{ job.template_name || $t('designHub.import_canva') }}</strong>
            <span class="hub-job-percent">{{ job.percent || 0 }}%</span>
          </div>
          <div class="hub-job-bar" role="progressbar" :aria-valuenow="job.percent || 0" aria-valuemin="0" aria-valuemax="100">
            <i :style="{ width: (job.percent || 0) + '%' }"></i>
          </div>
          <div class="hub-job-meta">
            <small>{{ jobLabel(job) }}</small>
          </div>
        </div>
      </div>
      <div class="hub-toolbar">
        <h5 class="hub-toolbar__title">{{ $t('designHub.your_designs') }}</h5>
        <div class="hub-toolbar__controls">
          <div class="hub-field">
            <label class="hub-field__label" for="hub-sort">{{ $t('designHub.sort_label') }}</label>
            <div class="hub-field__control hub-field__control--select">
              <select id="hub-sort" v-model="sortBy" class="hub-select">
                <option value="opened">{{ $t('designHub.sort_last_opened') }}</option>
                <option value="updated">{{ $t('designHub.sort_last_modified') }}</option>
                <option value="created">{{ $t('designHub.sort_date_created') }}</option>
                <option value="name_asc">{{ $t('designHub.sort_name_asc') }}</option>
                <option value="name_desc">{{ $t('designHub.sort_name_desc') }}</option>
              </select>
              <i class="fas fa-chevron-down hub-field__chevron" aria-hidden="true"></i>
            </div>
          </div>
          <div class="hub-field hub-field--grow">
            <label class="hub-field__label hub-field__label--sr" for="hub-search">{{ $t('designHub.search_placeholder') }}</label>
            <div class="hub-field__control hub-field__control--search">
              <i class="fas fa-search hub-field__icon" aria-hidden="true"></i>
              <input
                id="hub-search"
                v-model="query"
                type="search"
                class="hub-input"
                :placeholder="$t('designHub.search_placeholder')"
              />
            </div>
          </div>
        </div>
      </div>

      <div v-if="loading && !templates.length" class="text-muted py-4">{{ $t('designHub.loading_designs') }}</div>
      <div v-else-if="!filtered.length" class="hub-empty">
        <h5>{{ query ? $t('designHub.no_matching') : $t('designHub.no_saved_yet') }}</h5>
        <p>{{ query ? $t('designHub.try_different') : $t('designHub.create_to_start') }}</p>
        <button v-if="!query" class="btn btn-outline-primary mr-2" @click="openImport">{{ $t('designHub.import_canva') }}</button>
        <button v-if="!query" class="btn btn-primary" @click="openCreate">{{ $t('designHub.create_new') }}</button>
      </div>
      <div v-else class="hub-grid">
        <div v-for="t in filtered" :key="t.id" class="hub-card">
          <router-link :to="`/ai/studio/${t.id}`" class="hub-open">
            <div class="hub-thumb">
              <DesignPreview :template="t" fit="cover" flat />
            </div>
            <div class="hub-meta">
              <span :title="t.name">{{ t.name }}</span>
              <small>{{ formatDate(t.updated_at || t.created_at) }} · {{ t.width }}×{{ t.height }}</small>
            </div>
          </router-link>
          <button class="hub-del" :title="$t('common.delete')" @click.prevent="onDelete(t)">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </div>
    </ContentCard>

    <modal name="create-design-modal" height="auto" :adaptive="true" width="480">
      <div class="modal-pad">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">{{ $t('designHub.new_design') }}</h5>
          <button type="button" class="btn btn-sm btn-link" @click="$modal.hide('create-design-modal')">×</button>
        </div>
        <form @submit.prevent="onCreate">
          <div class="mb-3">
            <label class="small font-weight-bold">{{ $t('common.name') }}</label>
            <input v-model="form.name" class="form-control" required maxlength="80" :placeholder="$t('designHub.name_placeholder')" />
          </div>
          <div class="mb-3">
            <label class="small font-weight-bold">{{ $t('common.size') }}</label>
            <select v-model="form.sizeId" class="form-control">
              <option v-for="s in sizes" :key="s.id" :value="s.id">{{ s.name }} · {{ s.w }}×{{ s.h }}</option>
            </select>
          </div>
          <div class="text-right">
            <button type="button" class="btn btn-outline-secondary mr-2" @click="$modal.hide('create-design-modal')">{{ $t('common.cancel') }}</button>
            <button class="btn btn-primary" :disabled="creating">{{ creating ? $t('common.creating') : $t('common.create') }}</button>
          </div>
        </form>
      </div>
    </modal>

    <modal name="import-canva-modal" height="auto" :adaptive="true" width="480">
      <div class="modal-pad">
        <div class="import-modal-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0">{{ $t('designHub.import_title') }}</h5>
            <button type="button" class="btn btn-sm btn-link" @click="$modal.hide('import-canva-modal')">×</button>
          </div>
          <p class="small text-muted mb-3">{{ $t('designHub.canva_import_hint') }}</p>
          <form @submit.prevent="onImport">
            <div class="mb-3">
              <label class="small font-weight-bold">{{ $t('designHub.canva_link') }}</label>
              <input
                v-model="importForm.url"
                class="form-control"
                required
                :placeholder="$t('designHub.canva_url_placeholder')"
                :disabled="startingImport"
              />
            </div>
            <div class="text-right">
              <button type="button" class="btn btn-outline-secondary mr-2" @click="$modal.hide('import-canva-modal')">{{ $t('common.cancel') }}</button>
              <button class="btn btn-primary" :disabled="startingImport || !canImport">
                {{ startingImport ? $t('common.importing') : $t('common.import') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </modal>

    <modal name="canva-report-modal" height="auto" :adaptive="true" width="480">
      <div class="modal-pad">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">{{ $t('designStudio.report_canva_import') }}</h5>
          <button type="button" class="btn btn-sm btn-link" :disabled="canvaReportSending" @click="$modal.hide('canva-report-modal')">×</button>
        </div>
        <p class="small text-muted mb-3">{{ $t('designHub.report_import_failed_hint') }}</p>
        <form @submit.prevent="submitCanvaReport">
          <div class="mb-3">
            <label class="small font-weight-bold">{{ $t('designHub.canva_link') }}</label>
            <input
              v-model="canvaReportForm.url"
              class="form-control"
              required
              :placeholder="$t('designHub.canva_url_placeholder')"
              :disabled="canvaReportSending"
            />
          </div>
          <div class="mb-3">
            <label class="small font-weight-bold">{{ $t('designStudio.report_message') }}</label>
            <textarea
              v-model="canvaReportForm.message"
              class="form-control"
              rows="4"
              required
              maxlength="4000"
              :placeholder="$t('designHub.report_import_failed_placeholder')"
              :disabled="canvaReportSending"
            />
          </div>
          <div class="text-right">
            <button type="button" class="btn btn-outline-secondary mr-2" :disabled="canvaReportSending" @click="$modal.hide('canva-report-modal')">
              {{ $t('common.cancel') }}
            </button>
            <button type="submit" class="btn btn-primary" :disabled="canvaReportSending || !canvaReportForm.url.trim() || !canvaReportForm.message.trim()">
              {{ canvaReportSending ? $t('designStudio.report_sending') : $t('designStudio.send_report') }}
            </button>
          </div>
        </form>
      </div>
    </modal>
  </PageLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { SIZE_PRESETS, blankPage } from './studio/presets'
import DesignPreview from './studio/DesignPreview.vue'
import i18nPage from '@/mixins/i18nPage'

const SORT_STORAGE_KEY = 'designHub.sort'
const SORT_OPTIONS = ['opened', 'updated', 'created', 'name_asc', 'name_desc']

export default {
  components: { DesignPreview },
  mixins: [i18nPage],
  data() {
    return {
      query: '',
      sortBy: 'opened',
      creating: false,
      startingImport: false,
      pollTimer: null,
      seenStatus: {},
      sizes: SIZE_PRESETS,
      form: { name: '', sizeId: 'ig-post' },
      importForm: { url: '' },
      canvaReportSending: false,
      canvaReportForm: { url: '', message: '' },
    }
  },
  computed: {
    ...mapGetters('ai', ['templates', 'loading', 'canvaImportJobs']),
    importJobs() {
      return (this.canvaImportJobs || []).filter((job) => {
        const s = job.status
        return s && s !== 'done' && s !== 'error'
      })
    },
    filtered() {
      const q = this.query.toLowerCase().trim()
      let items = this.templates
      if (q) {
        items = items.filter((t) => String(t.name || '').toLowerCase().indexOf(q) !== -1)
      }
      return this.sortTemplates(items)
    },
    canImport() {
      return !!(this.importForm.url && this.importForm.url.trim())
    },
  },
  watch: {
    sortBy(value) {
      if (SORT_OPTIONS.indexOf(value) === -1) return
      try {
        localStorage.setItem(SORT_STORAGE_KEY, value)
      } catch (e) {
        /* ignore */
      }
    },
  },
  async created() {
    try {
      const saved = localStorage.getItem(SORT_STORAGE_KEY)
      if (saved && SORT_OPTIONS.indexOf(saved) !== -1) this.sortBy = saved
    } catch (e) {
      /* ignore */
    }
    await this.fetchTemplates()
    await this.refreshImportJobs()
    this.pollTimer = setInterval(() => this.refreshImportJobs(), 2000)
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer)
  },
  methods: {
    ...mapActions('ai', [
      'fetchTemplates', 'createTemplate', 'deleteTemplate', 'importCanva',
      'fetchCanvaImportJobs', 'fetchStudioFonts', 'reportCanvaImport',
    ]),
    openCreate() {
      this.form = { name: '', sizeId: 'ig-post' }
      this.$modal.show('create-design-modal')
    },
    openImport() {
      this.importForm = { url: '' }
      this.$modal.show('import-canva-modal')
    },
    openReportForJob(job) {
      if (!job) return
      const err = (job.error || job.message || '').trim()
      this.canvaReportForm = {
        url: (job.url || '').trim(),
        message: err && err !== this.$t('designHub.stage_error') ? err : '',
      }
      this.$modal.show('canva-report-modal')
    },
    async submitCanvaReport() {
      const url = (this.canvaReportForm.url || '').trim()
      const message = (this.canvaReportForm.message || '').trim()
      if (!url || !message) return
      this.canvaReportSending = true
      try {
        await this.reportCanvaImport({
          canva_url: url,
          message,
          design_template_id: '',
        })
        this.$modal.hide('canva-report-modal')
        this.$toast.success(this.$t('designStudio.report_sent'))
      } catch (e) {
        this.$toast.error((e.response && e.response.data && e.response.data.message) || this.$t('designStudio.report_failed'))
      } finally {
        this.canvaReportSending = false
      }
    },
    sortTemplates(items) {
      const list = items.slice()
      const time = (t, field) => {
        const v = t[field]
        if (!v) return 0
        const n = new Date(v).getTime()
        return Number.isNaN(n) ? 0 : n
      }
      const byName = (a, b, dir) => {
        const na = String(a.name || '').toLocaleLowerCase()
        const nb = String(b.name || '').toLocaleLowerCase()
        if (na < nb) return -dir
        if (na > nb) return dir
        return 0
      }
      switch (this.sortBy) {
        case 'opened':
          list.sort((a, b) => {
            const oa = time(a, 'opened_at')
            const ob = time(b, 'opened_at')
            if (ob !== oa) return ob - oa
            return time(b, 'updated_at') - time(a, 'updated_at')
          })
          break
        case 'created':
          list.sort((a, b) => time(b, 'created_at') - time(a, 'created_at'))
          break
        case 'name_asc':
          list.sort((a, b) => byName(a, b, 1))
          break
        case 'name_desc':
          list.sort((a, b) => byName(a, b, -1))
          break
        default:
          list.sort((a, b) => time(b, 'updated_at') - time(a, 'updated_at'))
      }
      return list
    },
    formatDate(value) {
      if (!value) return ''
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleDateString(this.intlLocale)
    },
    async onCreate() {
      const name = this.form.name.trim()
      if (!name) return
      const size = this.sizes.find((s) => s.id === this.form.sizeId) || this.sizes[0]
      this.creating = true
      try {
        const page = blankPage(size.w, size.h, '#ffffff')
        const t = await this.createTemplate({
          name,
          width: size.w,
          height: size.h,
          background_url: '',
          layers: { version: 2, pages: [page] },
        })
        this.$modal.hide('create-design-modal')
        this.$router.push(`/ai/studio/${t.id}`)
      } catch (e) {
        this.$toast.error((e.response && e.response.data && e.response.data.message) || this.$t('designHub.create_failed'))
      } finally {
        this.creating = false
      }
    },
    jobLabel(job) {
      if (job.status === 'downloading' && job.total) {
        return this.$t('designHub.import_progress', { done: job.done || 0, total: job.total })
      }
      const key = `designHub.stage_${job.status}`
      if (this.$te(key)) return this.$t(key)
      return job.error || job.message || ''
    },
    async refreshImportJobs() {
      try {
        const jobs = await this.fetchCanvaImportJobs()
        let added = false
        jobs.forEach((job) => {
          const prev = this.seenStatus[job.id]
          this.$set(this.seenStatus, job.id, job.status)
          if (prev && prev !== job.status) {
            if (job.status === 'done' && job.template_id) {
              added = true
              this.$toast.success(this.$t('designHub.stage_done'))
            } else if (job.status === 'error') {
              this.$toast.error(job.error || this.$t('designHub.import_failed'))
            }
          }
        })
        if (added) {
          await this.fetchTemplates()
          await this.fetchStudioFonts()
        }
      } catch (e) {
        /* keep polling */
      }
    },
    async onImport() {
      const url = (this.importForm.url || '').trim()
      if (!url) return
      this.startingImport = true
      try {
        await this.importCanva({ url })
        this.$modal.hide('import-canva-modal')
        this.$toast.success(this.$t('designHub.import_started'))
      } catch (e) {
        this.$toast.error((e.response && e.response.data && e.response.data.message) || this.$t('designHub.import_failed'))
      } finally {
        this.startingImport = false
      }
    },
    async onDelete(t) {
      const ok = await this.$confirm(this.$t('designHub.delete_confirm', { name: t.name }), {
        confirmText: this.$t('common.delete'),
        danger: true,
      })
      if (!ok) return
      try {
        await this.deleteTemplate(t.id)
        this.$toast.success(this.$t('designHub.deleted'))
      } catch (e) {
        this.$toast.error((e.response && e.response.data && e.response.data.message) || this.$t('designHub.delete_failed'))
      }
    },
  },
}
</script>

<style scoped>
.hub-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem 1.25rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}
.hub-toolbar__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.3;
  padding-bottom: 0.15rem;
}
.hub-toolbar__controls {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 0.65rem;
  flex: 1 1 16rem;
  justify-content: flex-end;
  min-width: 0;
}
.hub-field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 0;
}
.hub-field--grow {
  flex: 1 1 11rem;
  max-width: 16rem;
}
.hub-field__label {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #8b8a9a;
  line-height: 1;
}
.hub-field__label--sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.hub-field__control {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 2.25rem;
  border: 1px solid rgba(13, 12, 34, 0.12);
  border-radius: 10px;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.hub-field__control:focus-within {
  border-color: rgba(91, 77, 255, 0.55);
  box-shadow: 0 0 0 3px rgba(91, 77, 255, 0.12);
}
.hub-field__control--select {
  padding-right: 1.75rem;
  min-width: 10.5rem;
}
.hub-field__control--search {
  padding-left: 0.55rem;
}
.hub-field__icon {
  flex-shrink: 0;
  font-size: 0.8rem;
  color: #9a99a8;
  margin-right: 0.35rem;
}
.hub-field__chevron {
  position: absolute;
  right: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.65rem;
  color: #9a99a8;
  pointer-events: none;
}
.hub-select,
.hub-input {
  width: 100%;
  border: 0;
  background: transparent;
  font-size: 0.875rem;
  color: #0d0c22;
  padding: 0.4rem 0.65rem;
  min-width: 0;
  outline: none;
  appearance: none;
}
.hub-field__control--search .hub-input {
  padding-left: 0;
  padding-right: 0.5rem;
}
.hub-input::placeholder {
  color: #a8a7b5;
}
.hub-input[type='search']::-webkit-search-cancel-button {
  cursor: pointer;
}
@media (max-width: 575.98px) {
  .hub-toolbar__controls {
    width: 100%;
    justify-content: stretch;
  }
  .hub-field--grow {
    flex: 1 1 100%;
    max-width: none;
  }
  .hub-field__control--select {
    min-width: 0;
    width: 100%;
  }
}
.hub-jobs { margin-bottom: 1.25rem; display: grid; gap: 0.75rem; }
.hub-job {
  border: 1px solid rgba(13, 12, 34, 0.08);
  border-radius: 12px;
  padding: 0.85rem 1rem;
  background: #f7f8fb;
}
.hub-job.is-error { background: #fff5f5; }
.hub-job.is-done { background: #f3fbf6; }
.hub-job-top, .hub-job-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
}
.hub-job-top strong { font-size: 0.875rem; min-width: 0; }
.hub-job-percent {
  flex-shrink: 0;
  font-size: 0.875rem;
  font-variant-numeric: tabular-nums;
}
.hub-job-bar {
  height: 6px;
  margin: 0.55rem 0 0.45rem;
  border-radius: 99px;
  background: #e4e7ee;
  overflow: hidden;
}
.hub-job-bar i {
  display: block;
  height: 100%;
  background: #5b4dff;
  border-radius: 99px;
}
.hub-job.is-done .hub-job-bar i { background: #1f9d55; }
.hub-job.is-error .hub-job-bar i { background: #c1121f; }
.hub-job-meta small { color: #6d6c80; min-width: 0; }
.hub-empty {
  text-align: center;
  padding: 3.5rem 1rem;
  color: #6d6c80;
}
.hub-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(11.5rem, 1fr));
  gap: 1.25rem 1rem;
  align-items: stretch;
}
.hub-card {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-radius: 14px;
  padding: 0.35rem;
  transition: background 0.15s ease;
}
.hub-card:hover,
.hub-card:focus-within {
  background: rgba(13, 12, 34, 0.035);
}
.hub-open {
  display: flex;
  flex-direction: column;
  min-width: 0;
  color: inherit;
  text-decoration: none;
}
.hub-open:focus-visible {
  outline: 2px solid rgba(91, 77, 255, 0.45);
  outline-offset: 3px;
  border-radius: 12px;
}
.hub-thumb {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  background: #e8eaef;
  overflow: hidden;
  box-shadow: inset 0 0 0 1px rgba(13, 12, 34, 0.08);
  margin-bottom: 0.65rem;
}
.hub-thumb >>> .design-preview {
  position: absolute;
  inset: 0;
}
.hub-thumb >>> .design-preview__page {
  box-shadow: none;
  border-radius: 0;
}
.hub-meta {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-height: 3.25rem;
  padding: 0 0.15rem 0.2rem;
}
.hub-card span {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.3;
  min-height: 2.275em;
}
.hub-card small {
  color: #6d6c80;
  font-size: 0.75rem;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hub-del {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.94);
  color: #c1121f;
  cursor: pointer;
  opacity: 0;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(13, 12, 34, 0.12);
}
.hub-card:hover .hub-del,
.hub-card:focus-within .hub-del { opacity: 1; }
.modal-pad { padding: 1.25rem 1.4rem 1.4rem; }
.import-modal-body { position: relative; min-height: 160px; }
</style>
